import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Button, 
  Heading2, 
  Heading3,
  BodyText, 
  BodyLarge, 
  Caption,
  Flex,
  Spacer,
} from './ui';
import {
  MorphingButton,
  CursorAttractor,
  RippleContainer,
  BreathingCard,
} from '../styles/microInteractions';
import { DesignSystem, getColor, getSpacing, getShadow } from '../styles/designSystem';
import { 
  FadeInContainer, 
  FadeInUpContainer, 
  StaggerContainer
} from '../styles/animations';
import AppHeader from './Header';
import { useHeaderItineraries } from '../hooks/useHeaderItineraries';
import { API_BASE_URL } from '../api/tourism';

interface ReviewPageProps {
  onClose?: () => void;
  onNavigateTo?: (page: string) => void;
}

interface ReviewFormData {
  name: string;
  rating: number;
  photos: File[];
  review: string;
}

const ReviewPage: React.FC<ReviewPageProps> = ({ onClose, onNavigateTo }) => {
  const [formData, setFormData] = useState<ReviewFormData>({
    name: '',
    rating: 0,
    photos: [],
    review: '',
  });
  const [uploadedMediaPreview, setUploadedMediaPreview] = useState<string[]>([]);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const { destinationItineraries, trekkingItineraries, bikeItineraries } = useHeaderItineraries();

  // Handle file uploads
  const handleMediaUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newPreviews: string[] = [];

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          newPreviews.push(event.target.result as string);
          if (newPreviews.length === files.length) {
            setUploadedMediaPreview([...uploadedMediaPreview, ...newPreviews]);
            setFormData({ ...formData, photos: [...formData.photos, ...files] });
          }
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // Navigate media slideshow
  const nextMedia = () => {
    if (uploadedMediaPreview.length > 0) {
      setCurrentMediaIndex((prev) => (prev + 1) % uploadedMediaPreview.length);
    }
  };

  const prevMedia = () => {
    if (uploadedMediaPreview.length > 0) {
      setCurrentMediaIndex((prev) => (prev - 1 + uploadedMediaPreview.length) % uploadedMediaPreview.length);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.rating || !formData.review) {
      alert('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const today = new Date().toISOString().split('T')[0];

      const reviewData = {
        customer_name: formData.name,
        customer_image: null,
        rating: formData.rating,
        title: `Amazing experience in Ladakh`,
        review_text: formData.review,
        location: 'Ladakh',
        trip_date: today,
        media_items: uploadedMediaPreview.map(url => ({ type: 'image', url })),
        verified_booking: false
      };

      const response = await fetch(`${API_BASE_URL}/api/v1/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reviewData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit review');
      }

      setSubmitSuccess(true);
      window.dispatchEvent(new CustomEvent('review-submitted'));

      setTimeout(() => {
        setFormData({ name: '', rating: 0, photos: [], review: '' });
        setUploadedMediaPreview([]);
        setCurrentMediaIndex(0);
        setSubmitSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainContainer>
      <AppHeader 
        onNavigateTo={onNavigateTo} 
        currentPage="reviews"
        destinationItineraries={destinationItineraries}
        trekkingItineraries={trekkingItineraries}
        bikeItineraries={bikeItineraries}
      />
      
      {/* Hero Section */}
      <HeroSection>
        {onClose && (
          <CloseButton onClick={onClose}>
            ← Back
          </CloseButton>
        )}
        
        <HeroContent>
          <FadeInContainer>
            <div style={{ textAlign: 'center' }}>
              <Caption style={{ fontSize: '0.875rem', letterSpacing: '0.15em', marginBottom: getSpacing('md') }}>
                SHARE YOUR EXPERIENCE
              </Caption>
              <Spacer size="md" />
              <div style={{
                fontSize: DesignSystem.typography.scale.displayMedium.size,
                fontWeight: DesignSystem.typography.scale.displayMedium.weight,
                lineHeight: DesignSystem.typography.scale.displayMedium.lineHeight,
                letterSpacing: DesignSystem.typography.scale.displayMedium.letterSpacing,
                color: 'white',
                textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                marginBottom: getSpacing('xl'),
              }}>
                Your Journey Inspires Others
              </div>
              <BodyLarge style={{ color: 'rgba(255, 255, 255, 0.9)', maxWidth: '600px', margin: '0 auto' }}>
                Share your Himalayan adventure with our community. Your feedback helps us create unforgettable experiences.
              </BodyLarge>
            </div>
          </FadeInContainer>
        </HeroContent>
      </HeroSection>

      {/* Review Form Section */}
      <FormContainer>
        <Container maxWidth="lg">
          <FadeInUpContainer delay={0.1}>
            <FormContent>
              {submitSuccess ? (
                <SuccessMessage>
                  <SuccessIcon>✓</SuccessIcon>
                  <SuccessText>Thank you for sharing your experience!</SuccessText>
                  <SuccessSubtext>Your review helps us improve and inspire other travelers.</SuccessSubtext>
                </SuccessMessage>
              ) : (
                <StyledForm onSubmit={handleSubmit}>
                  {/* Name Input */}
                  <FormSection>
                    <SectionLabel>
                      <LabelText>Your Name</LabelText>
                      <RequiredAsterisk>*</RequiredAsterisk>
                    </SectionLabel>
                    <NameInput
                      type="text"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </FormSection>

                  {/* Rating Section */}
                  <FormSection>
                    <SectionLabel>
                      <LabelText>Rate Your Experience</LabelText>
                      <RequiredAsterisk>*</RequiredAsterisk>
                    </SectionLabel>
                    <RatingContainer>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <StarButton
                          key={star}
                          onClick={() => setFormData({ ...formData, rating: star })}
                          onMouseEnter={() => setHoveredRating(star)}
                          onMouseLeave={() => setHoveredRating(0)}
                          isActive={star <= (hoveredRating || formData.rating)}
                          type="button"
                        >
                          ★
                        </StarButton>
                      ))}
                      <RatingText>
                        {hoveredRating || formData.rating > 0 
                          ? `${hoveredRating || formData.rating} / 5 stars` 
                          : 'Click to rate'}
                      </RatingText>
                    </RatingContainer>
                  </FormSection>

                  {/* Media Upload Section */}
                  <FormSection>
                    <SectionLabel>
                      <LabelText>Photos & Videos (Optional)</LabelText>
                    </SectionLabel>
                    <UploadArea>
                      <UploadInput
                        type="file"
                        multiple
                        accept="image/*,video/*"
                        onChange={handleMediaUpload}
                        id="media-upload"
                      />
                      <UploadLabel htmlFor="media-upload">
                        <UploadIcon>📸</UploadIcon>
                        <UploadText>Click to upload or drag and drop</UploadText>
                        <UploadHint>PNG, JPG, MP4, MOV (Max 50MB)</UploadHint>
                      </UploadLabel>
                    </UploadArea>

                    {/* Media Preview Slideshow */}
                    {uploadedMediaPreview.length > 0 && (
                      <MediaGallery>
                        <GalleryHeader>
                          <GalleryTitle>Your Media ({uploadedMediaPreview.length})</GalleryTitle>
                          <RemoveAllButton 
                            onClick={() => {
                              setUploadedMediaPreview([]);
                              setFormData({ ...formData, photos: [] });
                              setCurrentMediaIndex(0);
                            }}
                            type="button"
                          >
                            Clear All
                          </RemoveAllButton>
                        </GalleryHeader>

                        <SlideShowContainer>
                          <MediaDisplay>
                            <img 
                              src={uploadedMediaPreview[currentMediaIndex]} 
                              alt={`Media ${currentMediaIndex + 1}`}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </MediaDisplay>

                          {uploadedMediaPreview.length > 1 && (
                            <>
                              <NavButton onClick={prevMedia} position="left">
                                ←
                              </NavButton>
                              <NavButton onClick={nextMedia} position="right">
                                →
                              </NavButton>
                            </>
                          )}

                          {/* Thumbnails */}
                          <ThumbnailContainer>
                            {uploadedMediaPreview.map((media, index) => (
                              <Thumbnail
                                key={index}
                                src={media}
                                alt={`Thumbnail ${index}`}
                                isActive={index === currentMediaIndex}
                                onClick={() => setCurrentMediaIndex(index)}
                              />
                            ))}
                          </ThumbnailContainer>

                          {/* Media Counter */}
                          <MediaCounter>
                            {currentMediaIndex + 1} / {uploadedMediaPreview.length}
                          </MediaCounter>
                        </SlideShowContainer>
                      </MediaGallery>
                    )}
                  </FormSection>

                  {/* Review Text Section */}
                  <FormSection>
                    <SectionLabel>
                      <LabelText>Your Review</LabelText>
                      <RequiredAsterisk>*</RequiredAsterisk>
                    </SectionLabel>
                    <ReviewTextArea
                      placeholder="Share your experience... What was your favorite moment? Any recommendations?"
                      value={formData.review}
                      onChange={(e) => setFormData({ ...formData, review: e.target.value })}
                      required
                      minLength={10}
                    />
                    <CharacterCount>
                      {formData.review.length} / 1000 characters
                    </CharacterCount>
                  </FormSection>

                  {/* Submit Button */}
                  <ButtonContainer>
                    <CursorAttractor attractionStrength={12}>
                      <RippleContainer>
                        <SubmitButton 
                          type="submit"
                          disabled={isSubmitting || !formData.name || !formData.rating || !formData.review}
                        >
                          {isSubmitting ? 'Submitting...' : 'Share Your Review'}
                        </SubmitButton>
                      </RippleContainer>
                    </CursorAttractor>
                  </ButtonContainer>
                </StyledForm>
              )}
            </FormContent>
          </FadeInUpContainer>
        </Container>
      </FormContainer>
    </MainContainer>
  );
};

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const MainContainer = styled.div`
  background: ${getColor('neutral.white')};
  min-height: 100vh;
  font-family: ${DesignSystem.typography.fonts.primary};
`;

const HeroSection = styled.section`
  position: relative;
  height: 50vh;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.5) 0%,
    rgba(0, 0, 0, 0.3) 50%,
    rgba(0, 0, 0, 0.6) 100%
  ), url('/leh_stok.jpg') center/cover;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: white;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 30% 70%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(245, 158, 11, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  background-attachment: fixed;

  @media (max-width: 768px) {
    height: 40vh;
    background-attachment: scroll;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${getSpacing('lg')};
  left: ${getSpacing('lg')};
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: ${getSpacing('sm')} ${getSpacing('md')};
  border-radius: ${DesignSystem.borderRadius.lg};
  cursor: pointer;
  font-weight: 500;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  backdrop-filter: blur(10px);
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 800px;
  padding: 0 ${getSpacing('lg')};
`;

const FormContainer = styled.section`
  padding: ${getSpacing('6xl')} ${getSpacing('lg')} ${getSpacing('6xl')};
  background: linear-gradient(135deg, ${getColor('primary.50')} 0%, ${getColor('neutral.white')} 100%);
  position: relative;
`;

const Container = styled.div<{ maxWidth?: string }>`
  max-width: ${props => {
    switch (props.maxWidth) {
      case 'sm': return '640px';
      case 'md': return '768px';
      case 'lg': return '1024px';
      case 'xl': return '1280px';
      default: return '100%';
    }
  }};
  margin: 0 auto;
  padding: 0 ${getSpacing('lg')};
`;

const FormContent = styled.div`
  background: ${getColor('neutral.white')};
  border-radius: ${DesignSystem.borderRadius['3xl']};
  padding: ${getSpacing('4xl')};
  box-shadow: ${getShadow('medium')};
  border: 1px solid ${getColor('primary.100')};

  @media (max-width: 768px) {
    padding: ${getSpacing('2xl')};
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${getSpacing('3xl')};
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${getSpacing('md')};
`;

const SectionLabel = styled.label`
  display: flex;
  align-items: baseline;
  gap: ${getSpacing('xs')};
`;

const LabelText = styled.span`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${getColor('primary.900')};
`;

const RequiredAsterisk = styled.span`
  color: ${getColor('semantic.error')};
  font-weight: 700;
`;

const NameInput = styled.input`
  padding: ${getSpacing('md')} ${getSpacing('lg')};
  border: 1px solid ${getColor('primary.200')};
  border-radius: ${DesignSystem.borderRadius.lg};
  font-size: 1rem;
  font-family: inherit;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  background: ${getColor('neutral.white')};
  color: ${getColor('primary.900')};

  &:focus {
    outline: none;
    border-color: ${getColor('primary.500')};
    box-shadow: 0 0 0 3px ${getColor('primary.100')};
    background: ${getColor('primary.50')};
  }

  &::placeholder {
    color: ${getColor('neutral.gray.400')};
  }
`;

const RatingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${getSpacing('lg')};
  flex-wrap: wrap;
`;

const StarButton = styled.button<{ isActive: boolean }>`
  font-size: 3rem;
  background: none;
  border: none;
  cursor: pointer;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  color: ${props => props.isActive ? getColor('accent.500') : getColor('neutral.gray.300')};
  transform: ${props => props.isActive ? 'scale(1.2)' : 'scale(1)'};

  &:hover {
    transform: scale(1.3);
    filter: drop-shadow(0 4px 12px rgba(245, 158, 11, 0.4));
  }

  &:active {
    transform: scale(1.1);
  }
`;

const RatingText = styled.span`
  font-weight: 600;
  color: ${getColor('primary.700')};
  margin-left: ${getSpacing('md')};
`;

const UploadArea = styled.div`
  border: 2px dashed ${getColor('primary.300')};
  border-radius: ${DesignSystem.borderRadius.lg};
  padding: ${getSpacing('3xl')} ${getSpacing('2xl')};
  text-align: center;
  transition: all ${DesignSystem.motion.duration.normal} ease;
  background: ${getColor('primary.50')};
  cursor: pointer;

  &:hover {
    border-color: ${getColor('primary.500')};
    background: ${getColor('primary.100')};
  }
`;

const UploadInput = styled.input`
  display: none;
`;

const UploadLabel = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${getSpacing('md')};
  cursor: pointer;
`;

const UploadIcon = styled.div`
  font-size: 3rem;
`;

const UploadText = styled.span`
  font-weight: 600;
  color: ${getColor('primary.900')};
  font-size: 1.125rem;
`;

const UploadHint = styled.span`
  font-size: 0.875rem;
  color: ${getColor('neutral.gray.600')};
`;

const MediaGallery = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${getSpacing('lg')};
  background: ${getColor('primary.50')};
  padding: ${getSpacing('2xl')};
  border-radius: ${DesignSystem.borderRadius.lg};
  border: 1px solid ${getColor('primary.200')};
`;

const GalleryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const GalleryTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${getColor('primary.900')};
  margin: 0;
`;

const RemoveAllButton = styled.button`
  background: ${getColor('semantic.error')};
  color: white;
  border: none;
  padding: ${getSpacing('sm')} ${getSpacing('md')};
  border-radius: ${DesignSystem.borderRadius.md};
  cursor: pointer;
  font-weight: 500;
  transition: all ${DesignSystem.motion.duration.fast} ease;

  &:hover {
    background: ${getColor('semantic.error')};
    opacity: 0.8;
    transform: translateY(-2px);
  }
`;

const SlideShowContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
`;

const MediaDisplay = styled.div`
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: ${DesignSystem.borderRadius.lg};
  overflow: hidden;
  background: ${getColor('neutral.white')};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${getShadow('soft')};
`;

const NavButton = styled.button<{ position: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${props => props.position === 'left' ? 'left' : 'right'}: -60px;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  background: ${getColor('primary.600')};
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.5rem;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${getColor('primary.700')};
    transform: translateY(-50%) scale(1.1);
    box-shadow: ${getShadow('medium')};
  }

  @media (max-width: 768px) {
    ${props => props.position === 'left' ? 'left' : 'right'}: 8px;
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }
`;

const ThumbnailContainer = styled.div`
  display: flex;
  gap: ${getSpacing('md')};
  margin-top: ${getSpacing('lg')};
  justify-content: center;
  overflow-x: auto;
  padding: 0 ${getSpacing('md')};

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${getColor('primary.100')};
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${getColor('primary.300')};
    border-radius: 10px;

    &:hover {
      background: ${getColor('primary.400')};
    }
  }
`;

const Thumbnail = styled.img<{ isActive: boolean }>`
  width: 70px;
  height: 70px;
  border-radius: ${DesignSystem.borderRadius.md};
  cursor: pointer;
  object-fit: cover;
  border: 3px solid ${props => props.isActive ? getColor('primary.600') : getColor('primary.200')};
  transition: all ${DesignSystem.motion.duration.fast} ease;
  flex-shrink: 0;

  &:hover {
    border-color: ${getColor('primary.500')};
    transform: scale(1.05);
  }
`;

const MediaCounter = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: ${getSpacing('sm')} ${getSpacing('md')};
  border-radius: ${DesignSystem.borderRadius.full};
  font-weight: 600;
  font-size: 0.875rem;
  backdrop-filter: blur(10px);
`;

const ReviewTextArea = styled.textarea`
  padding: ${getSpacing('lg')};
  border: 1px solid ${getColor('primary.200')};
  border-radius: ${DesignSystem.borderRadius.lg};
  font-size: 1rem;
  font-family: inherit;
  min-height: 200px;
  resize: vertical;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  background: ${getColor('neutral.white')};
  color: ${getColor('primary.900')};

  &:focus {
    outline: none;
    border-color: ${getColor('primary.500')};
    box-shadow: 0 0 0 3px ${getColor('primary.100')};
    background: ${getColor('primary.50')};
  }

  &::placeholder {
    color: ${getColor('neutral.gray.400')};
  }
`;

const CharacterCount = styled.div`
  text-align: right;
  font-size: 0.875rem;
  color: ${getColor('neutral.gray.600')};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-top: ${getSpacing('2xl')};
  border-top: 1px solid ${getColor('primary.100')};
`;

const SubmitButton = styled.button`
  padding: ${getSpacing('lg')} ${getSpacing('3xl')};
  font-size: 1.125rem;
  font-weight: 600;
  background: linear-gradient(135deg, ${getColor('primary.600')}, ${getColor('primary.700')});
  color: white;
  border: none;
  border-radius: ${DesignSystem.borderRadius.lg};
  cursor: pointer;
  transition: all ${DesignSystem.motion.duration.normal} ease;
  box-shadow: ${getShadow('medium')};
  letter-spacing: -0.01em;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${getShadow('large')};
    background: linear-gradient(135deg, ${getColor('primary.700')}, ${getColor('primary.800')});
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${getSpacing('lg')};
  padding: ${getSpacing('4xl')} ${getSpacing('2xl')};
  text-align: center;
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, ${getColor('semantic.success')}, #10B981);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: white;
  animation: scaleIn 0.5s ease;

  @keyframes scaleIn {
    from {
      transform: scale(0);
    }
    to {
      transform: scale(1);
    }
  }
`;

const SuccessText = styled.h2`
  font-size: 1.875rem;
  font-weight: 600;
  color: ${getColor('primary.900')};
  margin: 0;
`;

const SuccessSubtext = styled.p`
  font-size: 1rem;
  color: ${getColor('neutral.gray.600')};
  margin: 0;
  max-width: 500px;
`;

export default ReviewPage;
